import { Component, inject, OnDestroy } from '@angular/core';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';

// Icons
import { LucideAngularModule, CircleAlert, LoaderCircle, CircleCheck } from 'lucide-angular';

// API Services
import { AuthService, LoginRequest } from '@core/api/auth/auth.service';

// Component Services
import { ToastService } from '@shared/components/toast/toast.service';
import { UserSessionService } from '@core/services/user-session.service';

// Shared Components
import { InputComponent } from '@shared/forms/input/input.component';
import { ButtonComponent } from '@shared/forms/button/button.component';

// Validation
import { getFieldError, isFieldInvalid } from '@app/helpers/validators/custom-form-validation';
import { LoginValidationMessages } from '@helpers/validation-maps/login.map';

import { APP_CONSTANTS } from '@app/configs/app.constants';
import { getLocation } from '@app/helpers/utils/getLocation';

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [ReactiveFormsModule, LucideAngularModule, InputComponent, ButtonComponent, RouterLink],
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnDestroy {
    readonly CircleAlert = CircleAlert;
    readonly LoaderCircle = LoaderCircle;
    readonly CircleCheck = CircleCheck;

    private authService = inject(AuthService);
    private router = inject(Router);
    private toastService = inject(ToastService);
    private userSessionService = inject(UserSessionService);
    private destroy$ = new Subject<void>();

    loginForm = new FormGroup({
        email: new FormControl({ value: '', disabled: false }, [Validators.required, Validators.email]),
        password: new FormControl({ value: '', disabled: false }, [Validators.required]),
        otp: new FormControl({ value: '', disabled: false }, []),
    });

    isLoading = false;
    isResending = false;
    isSubmitted = false;
    otpStep = false; // For OTP step state
    timer = 0;
    expiryTime = 0;
    expiryDurationSeconds = 0; // Store server's expiry duration
    timerInterval: any; // For OTP timer interval
    otpExpired = false;
    otpVerified = false;
    otpLabel = 'OTP expires in:';

    /**
     *
     * Navigates to the registration page.
     * @returns void
     */
    onNavigateToRegister() {
        this.router.navigate(['/register']);
    }

    /**
     * Handles the form submission for login.
     * @returns void
     */
    async onSubmit() {
        this.isSubmitted = true;
        this.loginForm.markAllAsTouched();
        if (this.loginForm.invalid) {
            return;
        }
        this.isLoading = true;
        const location = await getLocation(); // temporarily get location here
        const loginRequest: LoginRequest = {
            email: this.loginForm.value.email!,
            password: this.loginForm.value.password!,
            geolocation: location ? JSON.stringify(location) : null,
        };
        this.authService
            .login(loginRequest)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    // If login requires OTP, switch to OTP step
                    if (res?.status === 'pending' && res.data?.expiry_duration_seconds) {
                        this.toastService.show({
                            title: 'Multi-Factor Authentication',
                            content:
                                res.message ||
                                'A one-time password (OTP) has been sent to your email. Please check your inbox to proceed.',
                            duration: 5000,
                            position: 'center',
                            type: 'info',
                        });

                        this.otpStep = true;
                        // Store the actual expiry duration from server
                        this.expiryDurationSeconds = res.data.expiry_duration_seconds;
                        this.otpExpired = false;
                        this.otpVerified = false;
                        this.otpLabel = 'OTP expires in:';
                        this.startTimer();
                        this.loginForm.get('email')?.disable();
                        this.loginForm.get('password')?.disable();

                        this.isSubmitted = false;

                        this.loginForm.get('otp')?.setValidators([
                            Validators.required,
                            Validators.minLength(6), // will be fetched in a configuration later
                            Validators.maxLength(6),
                            Validators.pattern('^[0-9]*$'),
                        ]);
                        this.loginForm.get('otp')?.markAsUntouched({
                            onlySelf: true,
                            emitEvent: false,
                        });
                        this.loginForm.get('otp')?.updateValueAndValidity();
                    } else {
                        this.router.navigate(['/home']);
                    }
                },
                error: (err) => {
                    this.toastService.show({
                        title: 'Login Failed',
                        content: err?.error?.details?.message || 'An error occurred during login. Please try again.',
                        duration: 5000,
                        position: 'center',
                        type: 'error',
                    });
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                },
            });
    }

    /**
     * Handles the OTP verification.
     * @returns void
     */
    async onVerifyOtp() {
        this.isSubmitted = true;
        if (this.loginForm.get('otp')?.invalid) return;
        this.isLoading = true;
        const location = await getLocation(); // temporarily get location here
        this.authService
            .verifyOtp({
                email: this.loginForm.getRawValue().email!,
                otp: this.loginForm.value.otp!,
                geolocation: location ? JSON.stringify(location) : null,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    this.otpVerified = true;

                    if (this.timerInterval) clearInterval(this.timerInterval);

                    // Fetch current user data and set it (app.component will handle Echo and session)
                    this.authService.getCurrentUser().subscribe({
                        next: (userResponse) => {
                            if (userResponse.data) {
                                this.authService.setUser(userResponse.data);
                            }

                            console.log('Navigating to:', !res?.data?.is_first_login ? '/home' : '/new-user-password');
                            if (!res?.data?.is_first_login) {
                                setTimeout(() => {
                                    this.router.navigate(['/home'], {
                                        replaceUrl: true,
                                        state: { showWelcome: true },
                                    });
                                }, 100);
                            } else {
                                setTimeout(() => {
                                    this.router.navigate(['/new-user-password'], {
                                        replaceUrl: true,
                                    });
                                }, 100);
                            }
                        },
                        error: () => {
                            // If getCurrentUser fails, navigate anyway (guard will handle it)
                            console.log('Navigating to:', !res?.data?.is_first_login ? '/home' : '/new-user-password');
                            if (!res?.data?.is_first_login) {
                                setTimeout(() => {
                                    this.router.navigate(['/home'], {
                                        replaceUrl: true,
                                        state: { showWelcome: true },
                                    });
                                }, 100);
                            } else {
                                setTimeout(() => {
                                    this.router.navigate(['/new-user-password'], {
                                        replaceUrl: true,
                                    });
                                }, 100);
                            }
                        },
                    });
                },
                error: (err) => {
                    console.error('OTP Verification Error:', err);
                    this.toastService.show({
                        title: 'OTP Verification Failed',
                        content: err?.error?.details?.message || 'Invalid OTP.',
                        duration: 5000,
                        position: 'center',
                        type: 'error',
                    });
                    this.isLoading = false;
                },
                complete: () => {
                    this.isLoading = false;
                },
            });
    }

    /**
     * Handles the OTP resend action.
     * @returns void
     */
    onResendOtp() {
        // Call backend to resend OTP
        this.isResending = true;
        this.authService
            .sendOtp({
                email: this.loginForm.get('email')?.value!,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res) => {
                    if (res?.data?.expiry_duration_seconds) {
                        this.expiryDurationSeconds = res.data.expiry_duration_seconds;
                        this.otpExpired = false;
                        this.otpLabel = 'OTP expires in:';
                        this.startTimer();
                        this.loginForm.get('otp')?.setValue('');
                        this.loginForm.get('otp')?.markAsUntouched({
                            onlySelf: true,
                            emitEvent: true,
                        });
                    }
                },
                error: (err) => {
                    this.toastService.show({
                        title: 'Resend OTP Failed',
                        content: err?.error?.details?.message || 'Failed to resend OTP.',
                        duration: 5000,
                        position: 'center',
                        type: 'error',
                    });
                    this.isResending = false;
                },
                complete: () => {
                    this.isResending = false;
                },
            });
    }

    // Update startTimer() method:
    startTimer() {
        if (this.timerInterval) clearInterval(this.timerInterval);

        const initialDuration = this.expiryDurationSeconds;
        let elapsedSeconds = 0;

        const updateTimer = () => {
            const remaining = Math.max(initialDuration - elapsedSeconds, 0);
            this.timer = remaining;

            if (remaining <= 0) {
                clearInterval(this.timerInterval);
                this.timerInterval = null;
                this.otpExpired = true;
                return;
            }

            elapsedSeconds++;
        };

        updateTimer();

        this.timerInterval = setInterval(() => {
            updateTimer();
        }, 1000);
    }

    /**
     * Formats the remaining time for the OTP timer.
     * @returns string in mm:ss format
     */
    getFormattedTimer(): string {
        const min = Math.floor(this.timer / 60);
        const sec = this.timer % 60;
        return `${min.toString().padStart(1, '0')}:${sec.toString().padStart(2, '0')}`;
    }

    isFieldInvalid(fieldName: string): boolean {
        return isFieldInvalid(this.loginForm, fieldName, this.isSubmitted);
    }

    getFieldError(fieldName: string): string | null {
        return getFieldError(this.loginForm, fieldName, LoginValidationMessages, this.isSubmitted);
    }

    /**
     * Cleans up subscriptions to prevent memory leaks.
     * @returns void
     */
    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
        if (this.timerInterval) clearInterval(this.timerInterval);
    }
}
